/**
 * Woocommerce — WooCommerce-specific helpers for reading checkout form state.
 *
 * This module is read-only with respect to the checkout form — it extracts
 * data from the DOM and returns it. All writes (carrier ID, pickup point,
 * form fields) are handled by ShiptimizeState.
 */
import Utils from './shiptimize-utils.js';

export default class Woocommerce {
  constructor(ajax_url = '') {
    this.ajax_url = ajax_url;
  }

  /** @returns {boolean} True if this is the WooCommerce checkout page */
  isCheckout() {
    return jQuery('body').hasClass('woocommerce-checkout');
  }

  /**
   * Get the currently selected shipping method ID from the checkout form.
   * Supports both block checkout and classic checkout.
   *
   * @returns {string} The numeric carrier ID extracted from the method ID, or ''
   */
  getShippingMethodId() {
    const isBlockCheckout = jQuery('.wc-block-checkout').length > 0;

    if (isBlockCheckout) {
      // Try data attribute set by WooCommerce Blocks
      const methodContainer = jQuery('[data-shipping-method-id]:first');
      if (methodContainer.length) {
        return Utils.removeNonNumeric(methodContainer.data('shipping-method-id'));
      }

      // Try WooCommerce Blocks JS store
      if (window.wc?.blocksStore?.getState) {
        try {
          const state = window.wc.blocksStore.getState();
          const active = (state.shippingRates || []).find((r) => r.selected);
          if (active) return Utils.removeNonNumeric(active.rate_id || active.id);
        } catch {}
      }

      return '';
    }

    // Classic checkout
    const eChecked = jQuery("input[name='shipping_method[0]']:checked");
    const eHidden  = jQuery("input[name='shipping_method[0]']");
    const method   = eChecked.length ? eChecked.val() : eHidden.val();

    if (!method || typeof method !== 'string') return '';

    return method.includes(':')
      ? Utils.removeNonNumeric(method.split(':')[0])
      : method;
  }

  /**
   * Read the current delivery address from the checkout form.
   * Supports both block checkout and classic checkout.
   *
   * Returns a plain address object — does not write to any global or field.
   *
   * @returns {{ Address: object }}
   */
  getShippingData() {
    const isBlockCheckout = jQuery('.wc-block-checkout').length > 0;

    let street, street2, city, postcode, state, country;

    if (isBlockCheckout) {
      street   = jQuery('#shipping-address_1').val() || jQuery('#billing-address_1').val() || '';
      street2  = jQuery('#shipping-address_2').val() || jQuery('#billing-address_2').val() || '';
      city     = jQuery('#shipping-city').val()      || jQuery('#billing-city').val()      || '';
      postcode = jQuery('#shipping-postcode').val()  || jQuery('#billing-postcode').val()  || '';
      state    = jQuery('#shipping-state').val()     || jQuery('#billing-state').val()     || '';

      // Country: try several selectors in order of reliability
      country = '';
      const countrySelectors = [
        '#components-form-token-input-0',
        'input[aria-label*="Country"]',
        '#shipping-country',
        '#billing-country',
      ];
      for (const sel of countrySelectors) {
        const val = jQuery(sel).val();
        if (val) { country = val; break; }
      }
    } else {
      const useSeparateShipping = jQuery('#ship-to-different-address input').is(':checked');
      const prefix              = useSeparateShipping ? 'shipping' : 'billing';

      street   = jQuery(`#${prefix}_address_1`).val() || '';
      street2  = jQuery(`#${prefix}_address_2`).val() || '';
      city     = jQuery(`#${prefix}_city`).val()      || '';
      postcode = jQuery(`#${prefix}_postcode`).val()  || '';
      state    = jQuery(`#${prefix}_state`).val()     || '';
      country  = jQuery(`#${prefix}_country`).val()   || '';
    }

    return {
      Address: {
        Lat:             '',
        Long:            '',
        Streetname1:     street,
        Streetname2:     street2,
        HouseNumber:     '',
        NumberExtension: '',
        PostalCode:      postcode,
        City:            city,
        Country:         country,
        State:           state,
      },
    };
  }
}
